/**
 * FloatingApprovalToggle - FAB for toggling approval mode
 *
 * Floating action button (bottom-right) to enable/disable approval mode in gallery.
 * Shows when there are pending images.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, X } from 'lucide-react';
import { useApprovalStats } from '@/hooks/useApproval';
import { cn } from '@/lib/utils';

export interface FloatingApprovalToggleProps {
  isApprovalMode: boolean;
  onToggle: () => void;
}

export function FloatingApprovalToggle({
  isApprovalMode,
  onToggle,
}: FloatingApprovalToggleProps) {
  const { t } = useTranslation();
  const { data: stats } = useApprovalStats();
  const [isExpanded, setIsExpanded] = useState(false);

  const pendingCount = stats?.pending || 0;

  // Auto-collapse after 3 seconds when expanded
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => setIsExpanded(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Don't show if no pending images and not in approval mode
  if (pendingCount === 0 && !isApprovalMode) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={cn(
          'flex items-center gap-3',
          'transition-all duration-300 ease-out'
        )}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Label (shows when expanded or when approval mode is active) */}
        <div
          className={cn(
            'bg-white dark:bg-neutral-800 rounded-full shadow-lg px-4 py-2',
            'transition-all duration-300 ease-out',
            isExpanded || isApprovalMode
              ? 'opacity-100 translate-x-0'
              : 'opacity-0 translate-x-8 pointer-events-none'
          )}
        >
          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {isApprovalMode ? t('toast.success.approvalSettingSaved') : t('common.pendingImages', { count: pendingCount })}
          </span>
        </div>

        {/* FAB Button */}
        <button
          onClick={onToggle}
          className={cn(
            'relative',
            'w-14 h-14 rounded-full',
            'shadow-xl',
            'flex items-center justify-center',
            'transition-all duration-300 ease-out',
            'hover:scale-110 active:scale-95',
            isApprovalMode
              ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white'
              : 'bg-gradient-to-br from-orange-500 to-orange-600 text-white'
          )}
          title={isApprovalMode ? t('common.deactivate') : t('common.activate')}
        >
          {isApprovalMode ? (
            <X className="h-6 w-6" />
          ) : (
            <CheckCircle className="h-6 w-6" />
          )}

          {/* Badge with pending count */}
          {!isApprovalMode && pendingCount > 0 && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">
              {pendingCount > 99 ? '99+' : pendingCount}
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
